const VisualComponent = require('./VisualComponent');
const {BehaviorTab} = require('./helpers');

const animatedClosing = (target, prefix, hide = 'false') => {
    if (target.hidden || target.classList.contains(prefix + '-hidden')) return;
    const handleTransitionEnd = e => {
        if (e.target === target) {
            if (hide === 'true') {
                target.hidden = true;
            } else {
                target.classList.add(prefix + '-hidden');
            }
            target.classList.remove(prefix + '-hiding');
            if (e.target.getAttribute('aria-hidden') === 'false') {
                // to mitigate quickly, clicking on the same trigger
                if (hide) {
                    target.hidden = false;
                } else {
                    target.classList.remove(prefix + '-hidden');
                }
            }
            target.removeEventListener('transitionend', handleTransitionEnd);
        }
    };
    target.addEventListener('transitionend', handleTransitionEnd);
    target.classList.add(prefix + '-hiding');
};

/**
 * @typedef {Object} ImageSource
 * @property {string} medium - default image source URL.
 * @property {string} [thumbnail] - thumbnail image src
 * @property {string} [large] - high resolution image src
 **/
/**
 * @typedef {Object} FigureInterface
 * @property {string} id - the figure id
 * @property {string} caption - the figure figcaption
 * @property {string} title - the figure title
 * @property {string} [notes] - if any notes are available in the figure (footnotes, attributions etc.)
 * @property {HTMLElement} ref - referenced element in the article
 * @property {string} content - <img> or <table> or <video> or <iframe> depending on the figure content
 * @property {HTMLElement} contentRef - referenced content element in the article
 * @property {[HTMLElement]} linkRefs - the figure referencing links in the article
 * @property {ImageSource} [src] - the figure image source(s)
 * @property {boolean} [smartCandidate] - Indicates if the table is a candidate for triggering any table-enhancing JS (for table only).
 * @property {number} figureType - 1 is image 0 is HTML useful for table data because in older articles tables were not HTML, and we need the zoom functionality to trigger
 * @property {string} type - "graphic" or "table" or "media" - this groups it into the 3 categories inside the figure viewer
 * @property {FigureInterface[]} [multiFigure] - the figure multi-image data
 *
 **/
/**
 * @param {FigureInterface} data - The figure data. eg:
 * {
 *     "id": the figure id
 *     "caption": figure figcaption,
 *     "title": figure title,
 *     "ref": referenced element in the article,
 *     "content": "<img src="#" loading="lazy">",
 *     "contentRef": referenced content element in the article,
 *     "linkRefs": [],
 *     "src": {
 *         "thumbnail": thumbnail image src
 *         "medium": default image src
 *         "large": high resolution image src
 *     },
 *     "figureType": 1,
 *     "type": "graphic" // "table" or "media"
 * }
 *
 * @param {object} view - The article view object.
 * @param {[]} [view.escapeModals] - An array to keep the order of what modals would react first to ESC key
 * @param {function} view.focusTrap - From the FocusTrap.js
 * @param {function} view.subscribe - From the EventManagers.js
 * @param {Object} view._texts - The available texts
 *
 * @param {object} config - The configuration object for the FV
 *
 * @param {object} config.info - the configuration options for the information
 * @param {object} config.info.aside - the configuration options for the information in the aside
 * @param {boolean} config.info.aside.rendersOpen - if the info panel renders open by default
 * @param {boolean} config.info.aside.hasClose - if the info panel has a close button
 * @param {boolean} config.info.aside.hasTitle - if the info panel has a separate row for the title
 * @param {[]} config.info.aside.content - the content that's going to render as figure caption
 *
 * @param {object} config.info.toolbar - the configuration options for the information in the toolbar
 * @param {string} config.info.toolbar.top - what part of the figure object to display in the top row
 * @param {string} config.info.toolbar.bottom - what part of the figure object to display in the bottom row
 *
 * @param {object} config.download - the configuration options for the download
 * @param {boolean} config.download.linkOption - what type link will be rendered at the standalone link
 * @param {boolean} config.download.panelOptions - what download type links will be rendered at the panel
 * @param {boolean} config.download.preventFor - the selector for the figures to prevent downloading for
 * @param {Object} config.download.renameSourceExt - the extension renaming for the download links
 *
 * @param {object} config.share - the configuration options for the share
 * @param {object} config.share.s - the selectors for the share-related elements
 *
 * @param {object} config.zoom - the configuration options for the zoom
 * @param {object} config.zoom.s - the selectors for the zoom-related elements
 * @param {string} config.zoom.s.zoomTrigger -
 * @param {object} config.zoom.options -
 * @param {number} config.zoom.options.initialZoom -
 *
 * @param {object} config.s - the configuration selectors
 * @param {string} config.s.parent - the element where the FV will append itself
 * @param {string} config.s.prefix - the FV prefix. All classes are expected to start with this
 * @param {string} config.s.panelTriggers - the class that will trigger opening the lightbox panels
 * @param {string} config.s.directory - the selector of the directory FV view
 * @param {string} config.s.lightbox - the selector of the lightbox FV view
 * @param {string} config.s.filmstrip - the selector for the filmstrip section
 * @param {string} config.s.frame - the selector for the triggers at the filmstrip section
 * @param {string} config.s.goBackToContent - the go-back-to-figure in the article link
 * @param {string} config.s.closeTriggers - the selector for all triggers that close the FV
 * @param {string} config.s.lockScreenClass - the class that adds screen lock
 * @param {string} config.s.template - the skeleton template for the FV
 * @param {string} config.s.slide - the selector for the slides in the carousel
 * @param {string} config.s.toolbar - the selector for the top toolbar
 * @param {string} config.s.holster - the selector for the top toolbar holster
 * @param {string} config.s.carouselArrows - the selector for the carousel arrow triggers
 *
 * @param {object} config.figure - the configuration options for each figure type
 * @param {object} config.figure.graphic - the configuration options for graphics
 * @param {object} config.figure.table - the configuration options for tables
 * @param {object} config.figure.video - the configuration options for media
 *
 * @param {object} templates - the Jade templates object
 * @param {function} templates.fvFrame - the Jade template for the filmstrip frame
 * @param {function} templates.fvAsideInfo - the Jade template for the aside info pane
 * @param {function} templates.fvAsideDownload - the Jade template for the aside download pane
 * @param {function} templates.fvTopInfo - the Jade template for the top info section
 * @param {function} templates.fvDirectoryItem - the Jade template for the directory items
 * @param {function} templates.fvLightboxFigures - the Jade template for the lightbox main section (carousel)
 *
 * @param {object} window.a2a - the AddThis object
 * @param {function} window.a2a.init_all - the AddThis function to initialize the share buttons
 * @param {object} window.MathJax - the MathJax object
 * @param {function} window.MathJax.typesetPromise - https://docs.mathjax.org/en/latest/advanced/typeset.html
 * @param {function} window.bc - https://player.support.brightcove.com/coding-topics/using-bc-and-getplayer-methods.html
 */
class FigureViewer extends VisualComponent {
    #initialized = false;
    #figureList = {};
    #s = {};
    #templates = {};
    #directory;
    #lightbox;

    _isOpen = false;
    _escModals = [];
    _closeBound = null;
    _texts = {};

    constructor(data, view, config, templates) {
        super();

        this.config = config;
        this.articleView = view;

        this.#s = config.s;
        this.#templates = {
            frame: templates.fvFrame,
            info: templates.fvAsideInfo,
            download: templates.fvAsideDownload,
            top: templates.fvTopInfo,
            item: templates.fvDirectoryItem,
            main: templates.fvLightboxFigures,
        };
        this._escModals = view?.escapeModals || [];
        this._texts = view._texts;

        for (const key in this.config.figure) {
            this.#figureList[key] = data[key];
        }

        this.open = this.open.bind(this);
        this.close = this.close.bind(this);

        // the FV should be initialized only when an element with [data-open="viewer"] is clicked, there is no need otherwise
        this.#enhancePageMarkup();
        this.#events.open(view.getArticle());
    }

    async open(id, index = 0) {
        if (!this.#initialized) {
            this.#initiate();
            this.#initialized = true;

            if (window.MathJax) {
                window.MathJax.typesetPromise && window.MathJax.typesetPromise();
            }
        }
        if (!this.main) {
            return console.debug('FigureViewer: main element not found');
        }

        if (!this._isOpen) {
            this._isOpen = true;
            document.body.classList.add(this.#s.lockScreenClass);
            this.main.hidden = false;
            this.main.removeAttribute('aria-hidden');
            this._closeBound = this.close.bind(this);
            this._escModals.push(this._closeBound);
        }

        if (id.includes(this.#s.prefix + '-panel_')) {
            await this.#directoryView.open(id);
        } else {
            await this.#lightboxView.open(id, index);
        }
        this.articleView.focusTrap?.init('figureViewer', this.main);
        this.#events.add();
    }

    close() {
        if (!this._isOpen) return;
        document.body.classList.remove(this.#s.lockScreenClass);
        this.closeTarget.call(this.main, this.#s.prefix, 'true');

        this.articleView.focusTrap?.deactivate();
        if (this._escModals && this._escModals.length) {
            const index = this._escModals.findIndex(fn => fn === this._closeBound);
            if (index !== -1) {
                this._escModals.splice(index, 1);
            }
        }

        this.#lightboxView.close();
        this.#directoryView.close();
        this.#events.remove();
        this._isOpen = false;
    }

    closeTarget(prefix, hide = 'false', animated = 'true') {
        const targetEl = this;
        if (targetEl.hidden || targetEl.classList.contains(prefix + '-hidden')) return;
        if (animated === 'true') {
            animatedClosing(targetEl, prefix, hide);
        } else {
            targetEl.classList.add(prefix + '-hidden');
        }
    }

    destroy() {
        this.main?.remove();
        this.#initialized = false;
        this.#lightboxView.zoom.destroy();
    }

    #initiate() {
        this.main = document.getElementById(this.#s.template)?.content.cloneNode(true);
        eval(this.#s.parent).appendChild(this.main);
        this.main = document.body.querySelector('.' + this.#s.prefix);
        if (this.main) {
            try {
                this.#directoryView.setup();
                this.#lightboxView.setup();
                this.#events.setup();
            } catch (error) {
                this.destroy();
                console.debug('FigureViewer initialization: ', error);
            }
        } else {
            return console.debug('FigureViewer: main element not found');
        }
    }

    #enhancePageMarkup() {
        /*
         * Find and enhance content that can trigger the figure viewer. Possible scenarios:
         * 1. add markup at images
         * 2. add links at the figure wrappers (popup or not)
         * 3. open from the content links
         */
        const config = this.articleView.config.figures;
        for (const key in this.config.figure) {
            if (key !== 'media') {
                //WK derived check, maybe it will need to be added as config
                const keyConfig = this.deepMerge(config.all, config[key]);
                const addRequiredAttributes = (el, id, index, str = 'viewer') => {
                    el.dataset.target = this.#s.prefix + '-' + id;
                    el.dataset.index = index;
                    el.setAttribute('data-open', str);
                };
                const createLink = (link, item, index) => {
                    const html = document.createElement('button');
                    !!keyConfig[link].classList && html.setAttribute('class', keyConfig[link].classList);
                    html.ariaLabel = this._texts[keyConfig[link].ariaLabel]?.replace(/##/g, item.title);
                    addRequiredAttributes(html, item.id, index);

                    let text = '';
                    if (keyConfig[link].text !== 'remove') {
                        text = document.createElement('span');
                        text.innerHTML = this._texts[keyConfig[link].text];
                    }
                    if (keyConfig[link].icon) {
                        const icon = document.createElement('i');
                        icon.classList = this.articleView.config.icons[keyConfig[link].icon];
                        icon.setAttribute('aria-hidden', 'true');
                        keyConfig[link].prependIcon ? html.append(icon, text) : html.append(text, icon);
                    } else {
                        html.append(text);
                    }
                    return html;
                };
                const renderFigure = (figure, content, index) => {
                    if (!content.multiFigure) {
                        //1
                        if (keyConfig['linkToViewerAlt'].enabled) {
                            addRequiredAttributes(content.contentRef, figure.id, index);
                        }
                        //2
                        if (keyConfig['linkToViewer'].enabled) {
                            const btn = createLink('linkToViewer', content, index);
                            if (keyConfig['linkToViewer'].classList.includes('figure-pop-btn')) {
                                content.ref.before(btn);
                            } else {
                                const parent = content.ref.closest('.figure-wrap');
                                const wrapper = parent.querySelector(keyConfig['linkToViewer'].customWrapper) || parent;
                                const nav = document.createElement('nav');
                                nav.appendChild(btn);
                                wrapper.appendChild(nav);
                            }
                        }
                    }
                    //3
                    if (this.config.figure[key].openFromBodyLinks) {
                        content.linkRefs.forEach(link => {
                            link.href = '#';
                            link.setAttribute(
                                'aria-label',
                                this._texts[keyConfig['linkToViewer'].ariaLabel]?.replace(/##/g, content.title)
                            );
                            link.setAttribute('role', 'button');
                            addRequiredAttributes(link, figure.id, '0', 'viewer inline');
                            link.addEventListener('keypress', e => {
                                const code = e.keyCode || e.which;
                                if (code === 13 || code === 32) {
                                    e.preventDefault();
                                    link.click();
                                }
                            });
                        });
                    }
                };
                this.#figureList[key].forEach(figure => {
                    renderFigure(figure, figure, '0');
                    figure.multiFigure?.forEach((el, i) => renderFigure(figure, el, i));
                });
            }
        }
    }

    #events = {
        setup: () => {
            if (this.articleView) {
                this.main
                    .querySelectorAll(this.#s.closeTriggers)
                    .forEach(btn => btn.addEventListener('click', this.close));
                this.#events.open(this.main);
                this.articleView.subscribe('articleInnerTargetOpen', this.close);
                this.articleView.subscribe('closeViewer', this.close);
            }
            this.#lightboxView.panel.events();
            this.#lightboxView.filmstrip.events();
            this.#lightboxView.mainArea.events();
        },
        add: () => {
            document.addEventListener('keydown', this.#events.keyDown);
        },
        remove: () => {
            document.removeEventListener('keydown', this.#events.keyDown);
        },
        open: (wrapper, selector = '[data-open~="viewer"]') => {
            wrapper.querySelectorAll(selector).forEach(el => {
                el.addEventListener('click', e => {
                    this.open(
                        e.currentTarget.getAttribute('data-target'),
                        e.currentTarget.getAttribute('data-index')
                    ).then(() => {});
                });
            });
        },
        keyDown: event => {
            if (this.#lightbox.dataset.zoomed === 'false') {
                const slides = this._active.carousel?.querySelectorAll(this.#s.slide);
                let index = Number(this._active.slideIndex);
                if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
                    event.preventDefault();
                    event.key === 'ArrowLeft' ? index-- : index++;
                    if (index >= 0 && index < slides.length) {
                        this.#lightboxView.setActiveFigure('#' + slides[index].id).then(() => {});
                    }
                }
            }
        },
    };
    #directoryView = {
        setup: () => {
            this.#directory = this.main.querySelector(this.#s.directory);
            if (!this.#directory) return;

            for (const key in this.config.figure) {
                const wrapper = this.#directory.querySelector('#' + this.#s.prefix + '-panel_' + key);
                if (wrapper) {
                    wrapper.innerHTML = this.#templates.item({
                        items: this.#figureList[key],
                        key,
                        prefix: this.#s.prefix,
                        triggerClass: this.#s.trigger,
                        overlayText: document.getElementById(this.#s.template).getAttribute('data-overlay'),
                    });
                }
            }
            new BehaviorTab(this.#directory, this.config.tabs);
            const tabs = this.#directory.querySelectorAll(this.config.tabs.buttonSelector);
            tabs.forEach(tab => {
                const type = tab.id.split(/[-_]/)[3] + 'Tab';
                const isDisabled = tab.getAttribute('aria-disabled');
                if (this.config.removeUnusedElements[type] && isDisabled === 'true') {
                    tab.remove();
                    this.#directory.querySelector('#' + tab.getAttribute('aria-controls')).remove();
                }
            });
        },
        open: id => {
            this.#directory.querySelectorAll('[loading="lazy"]').forEach(el => el.removeAttribute('loading'));
            const tab = this.#directory.querySelector('[aria-controls="' + id.replace('#', '') + '"]');
            tab?.click();
            this.#lightboxView.close();
            this.#directory.classList.remove(this.#s.prefix + '-hiding');
            this.#directory.hidden = false;
        },
        close: () => {
            animatedClosing(this.#directory, this.#s.prefix, 'true');
        },
    };
    #lightboxView = {
        _resetInterface: true,
        _active: {},
        _closePanelBound: [],

        setup: () => {
            this.#lightbox = this.main.querySelector(this.#s.lightbox);
            if (!this.#lightbox) return;

            this.triggers = {
                all: [...this.#lightbox.querySelectorAll(this.#s.panelTriggers)],
                filmstrip: [...this.#lightbox.querySelector(this.#s.filmstrip).querySelectorAll(this.#s.panelTriggers)],
                toolbar: [...this.#lightbox.querySelector(this.#s.holster).querySelectorAll(this.#s.panelTriggers)],
            };

            for (const key in this.config.figure) {
                this.#lightboxView.mainArea.setup(key);
                this.#lightboxView.filmstrip.setup(key);
            }
            this.#lightboxView.zoom.setup();
            this.#lightboxView.share.setup();

            const FocusMode = require('../../figure-viewer/FocusMode');
            this.focusMode = new FocusMode(this.#lightbox, this.config.focusMode);

            try {
                if (this.#figureList.media.length > 0) {
                    const host = this.#figureList.media[0].host;
                    this.#lightbox.querySelectorAll(this.articleView.config.videoPlayer[host].selector).forEach(videoEl => {
                        if (host === 'brightcove' && window.bc) {
                            if (!(videoEl.querySelector('video') || videoEl.classList.contains('vjs-plugins-ready'))) {
                                window.bc(videoEl);
                            }
                        } else if (host === 'kaltura') {
                            this.articleView.kalturaObserver()?.observe(videoEl);
                        }
                    });
                }
            } catch (error) {
                console.debug('Figure Viewer - video player init:', error);
            }
        },
        open: async (id, index) => {
            this._resetInterface = true;
            this.#directoryView.close();
            this.#lightbox.classList.remove(this.#s.prefix + '-hiding');
            this.#lightbox.hidden = false;
            await this.#lightboxView.setActiveFigure(id, index, 'instant');
            this.#lightbox.dataset.type = this._active.type || 'graphic';
            this._resetInterface = false;
            this.focusMode.init();
        },
        close: () => {
            this.#lightboxView.panel.reset();
            animatedClosing(this.#lightbox, this.#s.prefix, 'true');
            this.#lightbox.dataset.type = '';
            this._active?.slide?.classList.remove(this.articleView._classes.ACTIVE);
            this._active = {};
            this.focusMode.destroy();
        },
        setActiveFigure: async (id, index, behavior) => {
            await this.#lightboxView.zoom.waitForZoomReset();
            for (const key in this.config.figure) {
                const testId = id.replace(/#|core-fv-/g, '');
                const match = this.#figureList[key].filter(item => {
                    let id = testId === item.id;
                    if (!id && index && item.multiFigure) {
                        id = item.multiFigure[index].id === testId;
                    }
                    return id;
                });
                if (this._active?.slide) {
                    this._active.slide.inert = true;
                    this._active.slide.classList.remove(this.articleView._classes.ACTIVE);
                }
                if (match.length > 0) {
                    const multiFigure = match[0].multiFigure;
                    this._active = {
                        element: match[0],
                        id: match[0].id,
                        index: index || '0',
                        slide: this.#lightbox.querySelector('#' + this.#s.prefix + '-' + match[0].id),
                        type: match[0].type,
                        figureType: multiFigure ? multiFigure[index || 0].figureType : match[0].figureType,
                        figData: multiFigure ? multiFigure[index || 0] : match[0],
                    };
                    const figures = this._active.slide.querySelectorAll('.slide-content [data-index]');
                    multiFigure && figures.forEach(item => item.classList.remove(this.articleView._classes.ACTIVE));
                    this._active.carousel = this._active.slide.closest(this.#s.carousel);
                    this._active.figure =
                        Array.from(figures).find(fig => fig.dataset.index === this._active.index) ||
                        this._active.slide.querySelector('.slide-content');
                    this._active.slideIndex = this._active.slide.dataset.index;
                    this._active.figure.querySelector('[loading="lazy"]')?.removeAttribute('loading');
                    this.#lightboxView.waitForUpdatedInterface(behavior).then(() => {});
                    this.#lightboxView.zoom.update();
                    break;
                }
            }
        },
        waitForUpdatedInterface: async behavior => {
            this.#lightboxView.panel.update();
            this.#lightboxView.mainArea.update(behavior);
            this.#lightboxView.filmstrip.update();
            if (window.MathJax) {
                window.MathJax.typesetPromise && window.MathJax.typesetPromise();
            }
            await new Promise(resolve => setTimeout(resolve, 50));
        },

        panel: {
            open: (trigger, closeOthers = []) => {
                closeOthers = closeOthers.filter(
                    el => trigger.getAttribute('aria-controls') !== el.getAttribute('aria-controls')
                );
                closeOthers.forEach(el => this.#lightboxView.panel.close(el, 'false'));
                const control = trigger.getAttribute('aria-controls') || trigger.getAttribute('data-target');
                let target = this.#lightbox.querySelector('#' + control);
                if (control === this.#s.filmstrip.replace('.', '')) {
                    target = trigger.parentNode;
                }
                if (target) {
                    target.hidden = false;
                    target.classList.remove(this.#s.prefix + '-hidden');
                    target.classList.remove(this.#s.prefix + '-hiding');
                    target.classList.add(this.#s.prefix + '-visible');
                    target.setAttribute('aria-hidden', 'false');

                    if (this.focusMode._config.enabled) this.#lightbox.dataset.focusedDisabled = 'true';
                    this.#lightbox.dispatchEvent(new Event('resize'));
                }
                const triggers = this.#lightbox.querySelectorAll('[aria-controls="' + control + '"]');
                triggers.forEach(el => el.setAttribute('aria-expanded', 'true'));

                this.#lightboxView._closePanelBound.push(this.#lightboxView.panel.close.bind(this, trigger, 'true'));
                this._escModals.push(
                    this.#lightboxView._closePanelBound[this.#lightboxView._closePanelBound.length - 1]
                );
            },
            close: (trigger, animated = 'true') => {
                if (trigger.getAttribute('aria-expanded') === 'false') return;
                const control = trigger.getAttribute('aria-controls');
                let target = this.#lightbox.querySelector('#' + control);
                if (control === this.#s.filmstrip.replace('.', '')) {
                    target = trigger.parentNode;
                }

                if (target) {
                    target.classList.remove(this.#s.prefix + '-visible');
                    this.closeTarget.call(target, this.#s.prefix, 'false', animated);
                    target.setAttribute('aria-hidden', 'true');

                    if (this.focusMode._config.enabled) this.#lightbox.dataset.focusedDisabled = 'false';
                    this.#lightbox.dispatchEvent(new Event('resize'));
                }
                const triggers = this.#lightbox.querySelectorAll('[aria-controls="' + control + '"]');
                triggers.forEach(el => el.setAttribute('aria-expanded', 'false'));

                if (this._escModals && this._escModals.length) {
                    let index = this._escModals.findIndex(
                        fn => fn === this.#lightboxView._closePanelBound[this.#lightboxView._closePanelBound.length - 1]
                    );
                    if (index !== -1) {
                        let tempIndex = this.#lightboxView._closePanelBound.findIndex(
                            fn => fn === this._escModals[index - 1]
                        );
                        if (tempIndex !== -1) {
                            this.#lightboxView._closePanelBound.splice(tempIndex, 1);
                        }
                        this._escModals.splice(index, 1);
                    }
                }
            },
            toggle: (trigger, closeOthers = []) => {
                trigger && trigger.preventDefault && trigger.preventDefault();
                if (trigger.getAttribute('aria-expanded') === 'true') {
                    this.#lightboxView.panel.close(trigger);
                } else {
                    this.#lightboxView.panel.open(trigger, closeOthers);
                }
            },
            update: () => {
                this.#lightboxView.info.update();
                this.#lightboxView.updateBackToFigure();
                this.#lightboxView.download.update();
                this.#lightboxView.share.update();
            },
            reset: () => {
                this.triggers.all.forEach(controller => this.#lightboxView.panel.close(controller, 'false'));
            },
            events: () => {
                for (const [key] of Object.entries(this.triggers)) {
                    if (key !== 'all') {
                        this.triggers[key].forEach(controller => {
                            controller.addEventListener('click', () => {
                                this.#lightboxView.panel.toggle(
                                    controller,
                                    this.triggers[key].filter(el => el !== controller)
                                );
                            });
                        });
                    }
                }
            },
        },

        mainArea: {
            setup: key => {
                const lightbox = this.#lightbox;
                const s = this.#s;
                let wrapper = lightbox.querySelector('#' + s.prefix + '-carousel_' + key);
                if (wrapper) {
                    wrapper.innerHTML = this.#templates.main({
                        figures: this.#figureList[key],
                        prefix: s.prefix,
                        renderNotes: this.config.figure[key].repositionCaptionNotes,
                    });

                    if (this.config.removeUnusedElements.carouselControls && this.#figureList[key].length === 1) {
                        Array.from(this.#lightbox.querySelectorAll(this.#s.carouselArrows))
                            .filter(el => el.dataset.type === key)
                            .forEach(el => el.remove());
                    }

                    Array.from(wrapper.querySelectorAll('[id]'))
                        .filter(el => !el.id.startsWith(s.prefix))
                        .forEach(el => {
                            const newId = s.prefix + '-' + el.id;
                            const pointers = wrapper.querySelectorAll('[href="#' + el.id + '"]');
                            el.id = newId;
                            pointers.forEach(pointer => (pointer.href = '#' + newId));
                        });

                    // ['touchstart', 'mousedown', 'touchmove', 'mousemove', 'touchend', 'mouseup'].forEach((evt) => {
                    //     wrapper.addEventListener(evt, function(event){
                    //         if (lightbox.dataset.zoomed === 'false') {
                    //             const isTouchEvent = event.type === 'touchstart' || event.type === 'touchmove';
                    //             if (event.type === 'touchstart' || event.type === 'mousedown') {
                    //                 this.sx = isTouchEvent ? event.touches[0].clientX : event.clientX;
                    //             } else if (event.type === 'touchmove' || event.type === 'mousemove') {
                    //                 this.mx = isTouchEvent ? event.touches[0].clientX : event.clientX;
                    //             } else {
                    //                 if (Math.abs(this.mx - this.sx) > 25) {
                    //                     if (this.mx - this.sx < 0) {
                    //                         Array.from(lightbox.querySelectorAll(s.carouselArrows)).filter(el => el.classList.contains('next'))[0].click();
                    //                     } else {
                    //                         Array.from(lightbox.querySelectorAll(s.carouselArrows)).filter(el => el.classList.contains('previous'))[0].click();
                    //                     }
                    //                 }
                    //             }
                    //         }
                    //     }, true);
                    // });
                }
            },
            update: (behavior = 'smooth') => {
                if (!this._active.carousel || !this._active.slide) return;
                const {figure, figureType, figData, id, slide, type} = this._active;
                if (figureType === 'image') {
                    figure.querySelectorAll('img, svg').forEach(img => {
                        if (img.dataset.initialized !== 'true' && !img.parentNode.classList.contains('thumbnail')) {
                            img.removeAttribute('data-thumbnail-src');
                            img.removeAttribute('data-viewer-src');
                            img.setAttribute('src', figData.src.large || figData.src.medium || figData.src.thumbnail);
                            img.dataset.initialized = 'true';
                        }
                    });
                }
                this.#lightbox.querySelectorAll(this.#s.carouselArrows).forEach(arrow => {
                    const list = this.#figureList[type];
                    const index = list.findIndex(item => id === item.id);
                    if (arrow.classList.contains('previous') && index - 1 > -1) {
                        arrow.setAttribute('aria-controls', this.#s.prefix + '-' + list[index - 1].id);
                        arrow.disabled = false;
                    } else if (arrow.classList.contains('next') && index + 1 < list.length) {
                        arrow.setAttribute('aria-controls', this.#s.prefix + '-' + list[index + 1].id);
                        arrow.disabled = false;
                    } else {
                        arrow.disabled = true;
                    }
                });
                this._active.slide.inert = false;
                slide.classList.add(this.articleView._classes.ACTIVE);
                figure.classList.add(this.articleView._classes.ACTIVE);
                slide.scrollIntoView({behavior});
            },
            events: () => {
                this.#lightbox.querySelectorAll(this.#s.carouselArrows).forEach(btn => {
                    btn.addEventListener('click', () => {
                        this.#lightboxView.setActiveFigure(btn.getAttribute('aria-controls')).then(() => {});
                    });
                });
                this.#lightbox.addEventListener('resize', () => {
                    setTimeout(() => this._active.slide?.scrollIntoView({behavior: 'smooth'}), 200);
                    this.#lightboxView.zoom.currentViewer?.refresh();
                });
            },
        },
        filmstrip: {
            setup: key => {
                const wrapper = this.#lightbox.querySelector('#' + this.#s.prefix + '-filmstrip_' + key);
                if (wrapper) {
                    wrapper.innerHTML = this.#templates.frame({frames: this.#figureList[key], prefix: this.#s.prefix});
                }
            },
            update: () => {
                this.#lightbox.querySelectorAll(this.#s.frame).forEach(item => {
                    let target = item.getAttribute('aria-controls') === this.#s.prefix + '-' + this._active.id;
                    const index = (item.dataset.index || 0) === this._active.index;
                    if ((!item.dataset.index && target) || (target && index)) {
                        item.setAttribute('aria-selected', 'true');
                    } else {
                        item.setAttribute('aria-selected', 'false');
                    }
                });
            },
            events: () => {
                this.#lightbox.querySelectorAll(this.#s.frame).forEach(item => {
                    item.addEventListener('click', () => {
                        this.#lightboxView
                            .setActiveFigure(item.getAttribute('aria-controls'), item.dataset.index || 0)
                            .then(() => {});
                    });
                });
            },
        },

        info: {
            update: () => {
                this.#lightboxView.info.toolbar();
                this.#lightboxView.info.panel();
            },
            toolbar: () => {
                const config = this.config.info.toolbar;
                const {element, figData} = this._active;
                let bottom = element[config.bottom];
                if (element.multiFigure) {
                    const subItemTitle = figData.title;
                    bottom = bottom ? bottom + ' - ' + subItemTitle : subItemTitle;
                }
                this.#lightbox.querySelector(config.selector).innerHTML = this.#templates.top({
                    top: element[config.top] || sessionStorage.getItem('title'),
                    bottom,
                });
            },
            panel: () => {
                const config = this.config.info.aside;
                const {element, figData, type} = this._active;
                let content = {};
                config.content.forEach(item => {
                    if (
                        figData.figureType === 'image' ||
                        !(this.config.figure[type].repositionCaptionNotes && item === 'notes')
                    ) {
                        content[item] = element[item];
                    }
                });
                let subitem;
                if (element.multiFigure) {
                    subitem = figData.caption;
                }
                if (config.hasTitle) {
                    this.#lightbox.querySelector(config.s.title).innerHTML = element.title || '';
                }
                this.#lightbox.querySelector(config.s.text).innerHTML = this.#templates.info({content, subitem});
                const trigger = this.#lightbox.querySelector(this.#s.infoTrigger);
                const control = trigger.getAttribute('aria-controls') || trigger.getAttribute('data-target');
                const target = this.#lightbox.querySelector('#' + control);
                const text = target.querySelector(this.#s.panelText);
                text.parentNode.scrollTop = 0;
                if (window.innerWidth > 768 && config.rendersOpen && this._resetInterface) {
                    this.#lightboxView.panel.open(trigger, this.triggers.toolbar);
                }
                if (text.innerHTML === '') {
                    trigger.setAttribute('disabled', 'true');
                    this.#lightboxView.panel.close(trigger, 'false');
                } else {
                    trigger.removeAttribute('disabled');
                }
            },
        },
        download: {
            update: () => {
                const config = this.config.download;
                const {element, figData, index} = this._active;
                const figure = figData;
                const selectors = this.main.querySelectorAll('[class^="' + this.#s.prefix + '__download"]');
                selectors.forEach(el => {
                    if(!el.getAttribute('data-title')) {
                        el.setAttribute('data-title', el.title);
                    }
                    const hasPreventFor = !!element.ref.querySelector(config.preventFor);
                    const hasNoContent = figure.content === '';
                    const isGraphic = figData.figureType === 'image';
                    let links = {};

                    const hideDownload = () => {
                        el.classList.add('hidden');
                        if (el.getAttribute('aria-controls')) {
                            this.#lightboxView.panel.close(el, 'false');
                        }
                    }
                    const updateDownload = () => {
                        const href = !!figure.src ? figure.src.large || figure.src.medium || figure.src.thumbnail : figure.figureType;
                        const id = figure.id;
                        const doi = sessionStorage.getItem('doi');

                        let ext = href.split('.').pop();
                        ext = ext.length > 4 ? 'Figure' : ext.toUpperCase();
                        const types = {
                            img: [{
                                href: href.includes('?') ? href + '&download=true' : href + '?download=true',
                                ext,
                                label: config.renameSourceExt[ext.toUpperCase()] || ext,
                            }],
                            ppt: [{
                                href:
                                  doi && id
                                    ? `/action/downloadFigures?doi=${encodeURIComponent(
                                      doi
                                    )}&id=${id}&download=true`
                                    : null,
                                ext: 'PPT',
                                label: config.renameSourceExt['PPT'] || 'PPT',
                            }],
                            srcData: [{
                                ext: 'SRCDATA',
                                label: config.renameSourceExt['SRCDATA'] || 'Source Data',
                            }],
                        };
                        Array.from(config.panelOptions).forEach(item => {
                            const ext = types[item][0].ext;
                            if (figure.download?.[item]) {
                                types[item] = figure.download[item]
                                types[item].forEach(obj => {
                                    obj.ext = obj.ext || ext;
                                    obj.title = element.title;
                                    obj.linkTitle = obj.label;
                                    if(!obj.href.includes('download=true')) {
                                        obj.href = obj.href.includes('?') ? obj.href + '&download=true' : obj.href + '?download=true'
                                    }
                                });
                            }
                            types[item].forEach(obj => {
                                if (!links[item]) {
                                    links[item] = [];
                                }
                                if (obj.href) {
                                    links[item].push(obj);
                                }
                            });
                        });

                        el.classList.remove('hidden');
                        if (el.getAttribute('aria-controls')) {
                            this.#lightbox.querySelector(config.selector).innerHTML = this.#templates.download({
                                links,
                                options: config.panelOptions,
                            });
                        } else {
                            el.href = links[config.linkOption]?.[0].href || '#';
                            el.download = element.title; // download attribute works only with the same origin
                            el.title = links[config.linkOption]?.[0].linkTitle || el.getAttribute('data-title');
                            element.multiFigure ? (el.download += ' ' + element.multiFigure[index].title) : null;
                        }
                    }
                    if (hasPreventFor || hasNoContent) {
                        hideDownload();
                    } else if (!isGraphic) {
                        if (!figure.download) {
                            hideDownload();
                        } else {
                            updateDownload();
                        }
                    } else {
                        updateDownload();
                    }
                });
            },
        },
        share: {
            setup: () => {
                const config = this.config.share;
                const shareButtons = document.querySelector(config.s.buttons);
                const sharePane = this.#lightbox.querySelector(config.s.selector);
                if (!sharePane || !shareButtons) {
                    this.#lightbox.querySelector(config.s.trigger)?.remove();
                    return;
                }
                sharePane.innerHTML = shareButtons.outerHTML;
            },
            update: () => {
                const config = this.config.share;
                const shareButtonWrapper = this.#lightbox.querySelector(config.s.buttons);
                if (!shareButtonWrapper) return;
                shareButtonWrapper.setAttribute(
                    'data-a2a-url',
                    window.location.href.split('#')[0] + '#' + (this._active.figData.id || '')
                );
                shareButtonWrapper.setAttribute('data-a2a-title', sessionStorage.getItem('title'));
                shareButtonWrapper?.querySelectorAll('[class*="a2a_button"]').forEach(btn => {
                    btn.setAttribute('href', '#');
                    if (typeof window.a2a !== 'undefined') {
                        window.a2a.init_all();
                    }
                });
            },
        },
        zoom: {
            viewers: {
                graphic: [],
                table: [],
                media: [],
            },
            previousViewer: undefined,
            currentViewer: undefined,
            trigger: undefined,

            setup: () => {
                this.#lightboxView.zoom.trigger = this.#lightbox.querySelector(this.config.zoom.s.zoomTrigger);
                this.#lightboxView.zoom.trigger.addEventListener('click', () => {
                    if (this.#lightbox.dataset.zoomed === 'true') {
                        this.#lightboxView.zoom.waitForZoomReset().then(() => {});
                    } else {
                        this.#lightboxView.zoom.currentViewer?.zoom(this.config.zoom.options.initialZoom);
                        this.#lightboxView.zoom.currentViewer?.showSnapView(false);
                    }
                });
            },
            destroy: () => {
                this.#lightbox.dataset.zoomed = 'false';
                this.#lightboxView.zoom.currentViewer = undefined;
                this.#lightboxView.zoom.previousViewer = undefined;
            },
            update: () => {
                if (this._active.figData.figureType === 'image') {
                    this.#lightboxView.zoom.previousViewer = this.#lightboxView.zoom.currentViewer;
                    this.#lightboxView.zoom.trigger.parentNode.hidden = false;
                    const {viewers} = this.#lightboxView.zoom;
                    const {index, figure, slideIndex, type} = this._active;
                    let viewer = viewers[type][slideIndex];
                    if (viewer && viewer[index]) {
                        viewer = viewer[index];
                        viewer.refresh(false);
                    } else {
                        if (!viewer) {
                            viewers[type][slideIndex] = {};
                        }
                        const ImageViewer = require('../../figure-viewer/iv-viewer/ImageViewer');
                        viewers[type][slideIndex][index] = new ImageViewer(figure.querySelector('img'), {
                            ...this.config.zoom.options,
                            listeners: {
                                onZoomChange: ({reachedMin}) => {
                                    this.#lightbox.dataset.zoomed = String(!reachedMin);
                                },
                            },
                        });
                        viewer = viewers[type][slideIndex][index];
                    }
                    this.#lightboxView.zoom.currentViewer = viewer;
                    this.#lightboxView.zoom.previousViewer?.resetZoom(false);
                } else {
                    this.#lightboxView.zoom.trigger.parentNode.hidden = true;
                }
            },
            waitForZoomReset: async () => {
                while (this.#lightbox.dataset.zoomed === 'true') {
                    this.#lightboxView.zoom.currentViewer?.hideSnapView();
                    this.#lightboxView.zoom.currentViewer?.refresh();
                    await new Promise(resolve => setTimeout(resolve, 50));
                }
            },
        },
        updateBackToFigure: () => {
            this.#lightbox.querySelector(this.#s.goBackToContent).href = '#' + this._active.figData.id;
        },
    };
}

module.exports = FigureViewer;
